/**
 * Typed access to the measured template region map.
 *
 * template-regions.json is the single source of truth — it was measured from
 * Roblox's own template files, and Roblox has never published these
 * coordinates. Everything else keys off it, so this module also guards against
 * the two copies (JSON and format.ts) drifting apart.
 */
import regionData from "../assets/reference/template-regions.json";
import { CANVAS, REGION_IDS, type Rect, type RegionId } from "./format";

type RawRegions = Record<string, { x: number; y: number; w: number; h: number }>;

const raw = regionData.regions as unknown as RawRegions;

export const REGIONS: Record<RegionId, Rect> = Object.fromEntries(
  REGION_IDS.map((id) => {
    const r = raw[id];
    if (!r) throw new Error(`template-regions.json is missing region "${id}"`);
    return [id, { x: r.x, y: r.y, w: r.w, h: r.h }];
  }),
) as Record<RegionId, Rect>;

/** Uniform unpainted margin between adjacent regions. Measured, not assumed. */
export const GUTTER_PX: number = regionData.gutter.px;

/** True when a pixel lies inside any region (excluding gutters). */
export function regionAt(x: number, y: number): RegionId | null {
  for (const id of REGION_IDS) {
    const r = REGIONS[id];
    if (x >= r.x && x < r.x + r.w && y >= r.y && y < r.y + r.h) return id;
  }
  return null;
}

/** Region rect grown by the gutter, clamped to the canvas. */
export function regionWithGutter(id: RegionId, gutter = GUTTER_PX): Rect {
  const r = REGIONS[id];
  const x = Math.max(0, r.x - gutter);
  const y = Math.max(0, r.y - gutter);
  return {
    x,
    y,
    w: Math.min(CANVAS.width, r.x + r.w + gutter) - x,
    h: Math.min(CANVAS.height, r.y + r.h + gutter) - y,
  };
}

/** Pixels that never render on the avatar — 54.9% of the canvas, measured. */
export function deadSpaceArea(): number {
  const mapped = REGION_IDS.reduce((sum, id) => {
    const r = REGIONS[id];
    return sum + r.w * r.h;
  }, 0);
  return CANVAS.width * CANVAS.height - mapped;
}

/**
 * Region rectangles split at the R15 band boundaries.
 *
 * Sealing gaps in the position map has to stop at a band, not just at a region.
 * On R15 the torso region is two parts — UpperTorso owns the top 96 rows and
 * LowerTorso the rest — and a seal that only knows about regions fills the
 * boundary row from whichever side reaches it first. Measured: torso.front row
 * 170 ended up claimed by the upper half, so a brush there would have painted
 * LowerTorso's artwork onto UpperTorso's geometry.
 *
 * Passing these instead of REGIONS makes each band its own sealing area, so a
 * part can only ever be extended into rows it actually owns.
 *
 * Faces without a band — the 64x64 up/down caps — come through whole.
 */
export function bandedRegions(): Record<string, Rect> {
  const bands = regionData.r15PartBands as unknown as {
    torso: Array<{ offset: number; height: number }>;
    limb: Array<{ offset: number; height: number }>;
  };
  const out: Record<string, Rect> = {};
  for (const [id, rect] of Object.entries(REGIONS)) {
    const sliced = rect.h === 128 ? (id.startsWith("torso") ? bands.torso : bands.limb) : null;
    if (!sliced) {
      out[id] = rect;
      continue;
    }
    for (const band of sliced) {
      out[`${id}:${band.offset}`] = {
        x: rect.x,
        y: rect.y + band.offset,
        w: rect.w,
        h: band.height,
      };
    }
  }
  return out;
}
